/**
 * Entrada de código de traspaso (design-system §7 · Input "6 celdas" y research R-5).
 *
 * - Seis celdas de 56 px de alto, Nunito 800 28 px, auto-avance; una `<input>` real por celda
 *   (etiqueta propia "Carácter n de 6") dentro de un `role="group"` con la etiqueta visible.
 * - `inputmode="text"` + `autocapitalize="characters"`: el alfabeto de `HANDOFF_CODE_ALPHABET`
 *   mezcla letras y dígitos; todo se normaliza a mayúsculas y se descartan los caracteres que no
 *   están en el alfabeto (0, O, 1, I, espacios, guiones…).
 * - Pegar o autocompletar los 6 caracteres de una vez rellena todas las celdas.
 * - `value` es la cadena ya normalizada (sin huecos); `onComplete` se dispara al llegar a 6.
 */
import {
  useEffect,
  useId,
  useRef,
  type ChangeEvent,
  type ClipboardEvent,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';

import { HANDOFF_CODE_ALPHABET, HANDOFF_CODE_LENGTH } from '@fermin/contracts';

import { t } from '../../i18n';

import { cn } from './cn';

/** Mayúsculas, solo caracteres del alfabeto y como máximo `length` caracteres. */
export function normalizeCode(
  raw: string,
  alphabet: string = HANDOFF_CODE_ALPHABET,
  length: number = HANDOFF_CODE_LENGTH,
): string {
  let out = '';
  for (const char of raw.toUpperCase()) {
    if (alphabet.includes(char)) {
      out += char;
      if (out.length >= length) {
        break;
      }
    }
  }
  return out;
}

export interface CodeInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Se llama una vez cuando el código queda completo (6 caracteres válidos). */
  onComplete?: (value: string) => void;
  /** Etiqueta visible del grupo; por defecto `common.codeInput.label`. */
  label?: string;
  /** Oculta visualmente la etiqueta (sigue disponible para lectores de pantalla). */
  hideLabel?: boolean;
  length?: number;
  alphabet?: string;
  /**
   * Teclado del teléfono. `numeric` fuerza el teclado de números en iOS y Android (`inputMode` +
   * `pattern`); úsalo cuando el alfabeto solo tenga dígitos.
   */
  keyboard?: 'text' | 'numeric';
  invalid?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Si se indica, añade un `<input type="hidden">` con el valor para formularios nativos. */
  name?: string;
  /** Id de un texto de ayuda o error externo (`aria-describedby`). */
  describedBy?: string;
  className?: string;
}

export function CodeInput({
  value,
  onChange,
  onComplete,
  label,
  hideLabel = false,
  length = HANDOFF_CODE_LENGTH,
  alphabet = HANDOFF_CODE_ALPHABET,
  keyboard = 'text',
  invalid = false,
  disabled = false,
  autoFocus = false,
  name,
  describedBy,
  className,
}: CodeInputProps) {
  const labelId = useId();
  const cellRefs = useRef<Array<HTMLInputElement | null>>([]);
  const chars = normalizeCode(value, alphabet, length);
  const indexes = Array.from({ length }, (_, index) => index);
  // Valor más reciente, también entre `onChange` y el siguiente render: `handleFocus` se dispara
  // de forma síncrona al mover el foco tras un `commit` y no puede leer el `chars` del render.
  const latest = useRef(chars);
  useEffect(() => {
    latest.current = chars;
  }, [chars]);

  function focusCell(index: number): void {
    const clamped = Math.max(0, Math.min(index, length - 1));
    const cell = cellRefs.current[clamped];
    cell?.focus();
    cell?.select();
  }

  function commit(next: string, focusIndex: number): void {
    if (next !== chars) {
      latest.current = next;
      onChange(next);
      if (next.length === length) {
        onComplete?.(next);
      }
    }
    focusCell(focusIndex);
  }

  /** Escribe `typed` a partir de la celda `index`, sobrescribiendo lo que hubiera. */
  function insertAt(index: number, typed: string): void {
    if (typed.length === 0) {
      return;
    }
    if (typed.length >= length) {
      commit(typed.slice(0, length), length - 1);
      return;
    }
    const start = Math.min(index, chars.length);
    const next = (chars.slice(0, start) + typed + chars.slice(start + typed.length)).slice(0, length);
    commit(next, start + typed.length);
  }

  function handleChange(index: number, event: ChangeEvent<HTMLInputElement>): void {
    const raw = event.target.value;
    const current = chars[index] ?? '';
    // Si la celda ya tenía un carácter y el navegador concatenó el nuevo, nos quedamos con el nuevo.
    let candidate = raw;
    if (current && raw.length === 2) {
      candidate = raw.startsWith(current) ? raw.slice(1) : raw.slice(0, 1);
    }
    insertAt(index, normalizeCode(candidate, alphabet, length));
  }

  function handlePaste(index: number, event: ClipboardEvent<HTMLInputElement>): void {
    event.preventDefault();
    const typed = normalizeCode(event.clipboardData.getData('text'), alphabet, length);
    insertAt(index, typed);
  }

  function handleKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (chars.length === 0) {
        return;
      }
      const target = index < chars.length ? index : chars.length - 1;
      commit(chars.slice(0, target) + chars.slice(target + 1), target);
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusCell(index - 1);
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusCell(Math.min(index + 1, chars.length));
    }
  }

  function handleFocus(index: number, event: FocusEvent<HTMLInputElement>): void {
    // Nunca dejamos huecos: si toca una celda posterior a la primera vacía, movemos el foco allí.
    if (index > latest.current.length) {
      focusCell(latest.current.length);
      return;
    }
    event.target.select();
  }

  return (
    <div className={cn('flex flex-col gap-fm-2', className)}>
      <span
        id={labelId}
        className={cn('text-fm-base font-medium text-fm-text', hideLabel && 'sr-only')}
      >
        {label ?? t('common.codeInput.label')}
      </span>
      <div
        role="group"
        aria-labelledby={labelId}
        aria-describedby={describedBy}
        className="flex gap-fm-2"
      >
        {indexes.map((index) => (
          <input
            key={index}
            ref={(element) => {
              cellRefs.current[index] = element;
            }}
            type="text"
            inputMode={keyboard}
            {...(keyboard === 'numeric' ? { pattern: '[0-9]*' } : {})}
            autoCapitalize={keyboard === 'numeric' ? 'off' : 'characters'}
            autoCorrect="off"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            spellCheck={false}
            enterKeyHint="done"
            autoFocus={autoFocus && index === 0}
            disabled={disabled}
            value={chars[index] ?? ''}
            aria-label={t('common.codeInput.cell', { index: index + 1, total: length })}
            aria-invalid={invalid ? true : undefined}
            onChange={(event) => handleChange(index, event)}
            onPaste={(event) => handlePaste(index, event)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onFocus={(event) => handleFocus(index, event)}
            className={cn(
              'fm-tabular h-fm-control-lg min-w-0 flex-1 basis-0 rounded-fm-md border border-fm-border bg-fm-surface',
              'text-center font-display text-fm-2xl font-extrabold text-fm-text caret-fm-primary',
              'transition-colors duration-fm-fast ease-fm-standard motion-reduce:transition-none',
              'focus:border-fm-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus',
              'aria-[invalid=true]:border-fm-danger disabled:opacity-45',
            )}
          />
        ))}
      </div>
      {name ? <input type="hidden" name={name} value={chars} /> : null}
    </div>
  );
}
